/** Client-side helpers for attachment validation + rendering.
 *
 * Mirrors the server-side limits in
 * ``packages/api/pencilout/api/services/attachments.py`` so the UI
 * can reject bad files before they hit the wire. The server still
 * re-validates — these are an early-error UX layer, not a security
 * boundary.
 */

export const ALLOWED_IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/webp",
] as const;

export const ALLOWED_PDF_MIME_TYPES = ["application/pdf"] as const;

export const ALLOWED_MIME_TYPES = [...ALLOWED_IMAGE_MIME_TYPES, ...ALLOWED_PDF_MIME_TYPES] as const;

/** Comma-separated string for an ``<input type="file">`` ``accept`` attribute. */
export const ACCEPT_ATTR = ALLOWED_MIME_TYPES.join(",");

/** Cap matches server. */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
/** Cap matches server. */
export const MAX_PDF_BYTES = 20 * 1024 * 1024;
/** Per-comment attachment count cap (server-enforced). */
export const MAX_ATTACHMENTS_PER_COMMENT = 10;

export type AttachmentKind = "image" | "pdf" | "other";

/** Buckets a file or response shape into one of the three render
 *  modes the UI cares about — image (thumbnail), pdf (icon), other
 *  (rejected pre-flight, but kept here for defensive rendering of
 *  legacy rows). */
export function attachmentKind(mime: string): AttachmentKind {
  const m = mime.toLowerCase();
  if ((ALLOWED_IMAGE_MIME_TYPES as readonly string[]).includes(m)) return "image";
  if ((ALLOWED_PDF_MIME_TYPES as readonly string[]).includes(m)) return "pdf";
  return "other";
}

export interface AttachmentValidationFailure {
  file: File;
  reason: string;
}

/** Validate a single file against the MIME + size policy. Returns
 *  ``null`` on success, or a human-readable reason. */
export function validateAttachment(file: File): string | null {
  const mime = (file.type || "").toLowerCase();
  if (!(ALLOWED_MIME_TYPES as readonly string[]).includes(mime)) {
    return `${file.name}: unsupported file type (${file.type || "unknown"}). Use JPG, PNG, HEIC, WEBP or PDF.`;
  }
  if ((ALLOWED_IMAGE_MIME_TYPES as readonly string[]).includes(mime)) {
    if (file.size > MAX_IMAGE_BYTES) {
      return `${file.name}: image exceeds ${Math.round(MAX_IMAGE_BYTES / 1024 / 1024)} MB limit.`;
    }
  } else if (file.size > MAX_PDF_BYTES) {
    return `${file.name}: PDF exceeds ${Math.round(MAX_PDF_BYTES / 1024 / 1024)} MB limit.`;
  }
  return null;
}

/** Run all files through :func:`validateAttachment` and split into
 *  accepted + rejected. Caller decides how to surface the rejections
 *  (toast, inline error, etc.). */
export function partitionAttachments(files: File[]): {
  accepted: File[];
  rejected: AttachmentValidationFailure[];
} {
  const accepted: File[] = [];
  const rejected: AttachmentValidationFailure[] = [];
  for (const file of files) {
    const reason = validateAttachment(file);
    if (reason) {
      rejected.push({ file, reason });
    } else {
      accepted.push(file);
    }
  }
  return { accepted, rejected };
}

/** Strip the extension off a filename for use as a default label
 *  suggestion when promoting a comment-attachment to a job. */
export function filenameWithoutExtension(name: string): string {
  const idx = name.lastIndexOf(".");
  if (idx <= 0) return name;
  return name.slice(0, idx);
}
